import { useTranslation } from 'react-i18next';

import * as St from './MaterialCard.styles';

import * as cons from '~/core/constants';
import formatUOM from '~/core/helpers/formatUOM';
import { MaterialModel } from '~/core/models';
import { imgUrl } from '~/core/utils';
import DescriptionAndRating from '~atoms/DescriptionAndRating';
import type { DescriptionAndRatingData } from '~atoms/DescriptionAndRating';

type PriceRange = {
  min: number;
  max: number;
};

type Specification = {
  title: string;
  rating: number;
};

export type MaterialCardData = {
  id: string;
  img: { src: string; alt?: string };
  title: string;
  priceRange: PriceRange;
  specifications: Specification[];
  qteGrpId: MaterialModel['qteGrpID'];
};

export type MaterialCardProps = Omit<
  React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement>,
  'onClick' | 'ref'
> & {
  data: MaterialCardData;
  onSelect?: () => void;
  isSelected?: boolean;
};

const formatPriceRange = (price: PriceRange) =>
  [
    cons.CURRENCY_NO_CENTS.format(Math.floor(Number(price.min))),
    cons.RANGE_SEPARATOR,
    cons.CURRENCY_NO_CENTS.format(Math.ceil(Number(price.max))),
    cons.SPACE,
    formatUOM(cons.UNIT_MEASURE.MATERIALS),
  ].join('');

const MaterialCard = ({ data, onSelect, ...props }: MaterialCardProps) => {
  const translation = useTranslation();
  const t = (n: number) =>
    translation.t(`SCROLLABLE_AREA.MATERIAL.RATINGS.${[n - 1]}`);
  const specToRating = (spec: Specification): DescriptionAndRatingData => ({
    description: spec.title,
    rating: {
      text: t(spec.rating),
      value: spec.rating,
    },
  });

  return (
    <St.Container
      {...props}
      withHover
      onClick={() => onSelect?.()}
      onKeyDown={(event) => {
        if (event.code === 'Space') {
          event.preventDefault();
          onSelect?.();
        }
      }}
    >
      <St.Content>
        <St.Figure>
          <img src={imgUrl(data.img.src, 600)} alt={data.img.alt} />
          <figcaption>{data.title}</figcaption>
        </St.Figure>

        <St.PriceRange>
          <span>Price Range</span>
          <span>{formatPriceRange(data.priceRange)}</span>
        </St.PriceRange>

        {data.specifications.length > 0 && (
          <St.SpecificationsList aria-label="Product Specifications">
            {data.specifications.map(specToRating).map((spec) => (
              <li key={spec.description}>
                <DescriptionAndRating data={spec} />
              </li>
            ))}
          </St.SpecificationsList>
        )}
      </St.Content>
    </St.Container>
  );
};

export default MaterialCard;
